import { useQuery } from '@apollo/client'
import { get } from 'lodash'
import {
  ARTICLES_WIDGET_LIMIT,
  COLLECTION_WIDGET_LIMIT,
  COLLECTION_SORTER_FIELDS,
  SORTER_DIRECTION,
  PRODUCT_STATUSES,
  PRODUCT_VARIANT_STATUSES,
} from 'constants/common'
import { useUserAuth } from 'providers/userAuth'
import {
  type CmsPage,
  type CmsWidget,
  CmsConsultationPageBySlugDocument as CMS_CONSULTATION_PAGE_BY_SLUG_QUERY,
  CmsConsultationPagePreviewDocument as CMS_CONSULTATION_PAGE_PREVIEW,
} from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { isConsultationPage } from '../utils'

interface UseCmsConsultationPageBySlugProps {
  slug: string
  isConsultationsIndexPage?: boolean
  isPreview?: boolean
}

export const useCmsConsultationPageBySlug = ({
  slug,
  isConsultationsIndexPage,
  isPreview = false,
}: UseCmsConsultationPageBySlugProps) => {
  const { isPageLoading } = useUserAuth()
  const media = useMedia()

  const widgetLimits = media.MOBILE
    ? {
        articles: ARTICLES_WIDGET_LIMIT.MOBILE,
        collections: COLLECTION_WIDGET_LIMIT.MOBILE,
      }
    : {
        articles: ARTICLES_WIDGET_LIMIT.DESKTOP,
        collections: COLLECTION_WIDGET_LIMIT.DESKTOP,
      }

  const { data, loading: isLoading } = useQuery(
    isPreview
      ? CMS_CONSULTATION_PAGE_PREVIEW
      : CMS_CONSULTATION_PAGE_BY_SLUG_QUERY,
    {
      fetchPolicy: 'cache-first',
      skip: isConsultationPage({ slug, isConsultationsIndexPage }),
      variables: {
        slug,
        widgetsDefaultInputs: {
          cmsExpertsInput: {
            page: 1,
          },
          cmsArticlesInput: {
            page: 1,
            limit: widgetLimits.articles,
          },
          cmsMultiCollectionProductsInput: {
            pagination: {
              page: 1,
              limit: widgetLimits.collections,
            },
            sorter: {
              field: COLLECTION_SORTER_FIELDS.POSITION_INDEX_WEIGHT,
              direction: SORTER_DIRECTION.ASC,
            },
            filter: {
              statuses: [PRODUCT_STATUSES.PUBLISHED],
              variantStatuses: [PRODUCT_VARIANT_STATUSES.PUBLISHED],
            },
          },
        },
      },
    }
  )

  const pageBySlug: CmsPage = get(
    data,
    isPreview ? 'cmsConsultationPagePreview' : 'cmsConsultationPageBySlug'
  )
  const widgets: CmsWidget[] = get(pageBySlug, 'cmsPage.widgets', [])

  return {
    pageBySlug,
    widgets,
    isLoading: (isPreview && isPageLoading) || isLoading,
  }
}
